import type { ApiMessage } from '../../types/messages';
import type { OpenImRawMessage, OpenImRawPictureBaseInfo, OpenImRawPictureElem } from './types';

import {
  isOpenImGroupNotification,
  mapOpenImGroupNotificationContent,
} from './groupNotification';

// `constant.Picture/Sound/Video/File = 102/103/104/105` (openim-sdk-core `pkg/constant/constant.go`).
const OPENIM_CONTENT_TYPE_TEXT = 101;
const OPENIM_CONTENT_TYPE_PICTURE = 102;
const OPENIM_CONTENT_TYPE_SOUND = 103;
const OPENIM_CONTENT_TYPE_VIDEO = 104;
const OPENIM_CONTENT_TYPE_FILE = 105;
const OPENIM_CONTENT_TYPE_QUOTE = 114;
const OPENIM_CONTENT_TYPE_CUSTOM = 110;

// OpenIM sessionType (openim-sdk-core `pkg/constant/constant.go`): 1 = single/1:1 chat,
// 2 = normal ("write") group, 3 = super ("read") group.
const OPENIM_SESSION_TYPE_GROUP = 2;
const OPENIM_SESSION_TYPE_SUPER_GROUP = 3;
const OPENIM_PRIVATE_CHAT_ID_PREFIX = 'si_';

function cleanText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function tryParseJsonObject(raw: string): Record<string, unknown> | undefined {
  const trimmed = raw.trim();
  if (!trimmed.startsWith('{')) return undefined;
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    // not JSON
  }
  return undefined;
}

function tryDecodeBase64Json(raw: string): Record<string, unknown> | undefined {
  try {
    const decoded = atob(raw.trim());
    return tryParseJsonObject(decoded);
  } catch {
    return undefined;
  }
}

function parseContentObject(raw: OpenImRawMessage): Record<string, unknown> | undefined {
  const content = raw.content;
  if (content && typeof content === 'object' && !Array.isArray(content)) {
    return content as Record<string, unknown>;
  }
  if (typeof content === 'string' && content) {
    return tryParseJsonObject(content) || tryDecodeBase64Json(content);
  }
  return undefined;
}

// Mirrors Android `resolveHttpMediaUrl`: only accept remote http(s); upgrade http→https so
// https://web.twzkj.cn does not block mixed-content image loads. Local device paths are ignored.
function resolveHttpMediaUrl(url: unknown): string | undefined {
  if (typeof url !== 'string') return undefined;
  const trimmed = url.trim();
  if (!trimmed) return undefined;
  if (trimmed.startsWith('https://')) return trimmed;
  if (trimmed.startsWith('http://')) return `https://${trimmed.slice('http://'.length)}`;
  return undefined;
}

function firstHttpUrl(...candidates: unknown[]): string | undefined {
  for (const c of candidates) {
    const url = resolveHttpMediaUrl(c);
    if (url) return url;
  }
  return undefined;
}

function asPictureInfo(value: unknown): OpenImRawPictureBaseInfo | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  return value as OpenImRawPictureBaseInfo;
}

// Mirrors Android `normalizePictureMessageJson` / `pickImageUrl`: history/WS often store the
// whole PictureElem inside the `content` JSON string, with no top-level `pictureElem`.
function resolvePictureElem(raw: OpenImRawMessage): OpenImRawPictureElem | undefined {
  if (raw.pictureElem && typeof raw.pictureElem === 'object') {
    return raw.pictureElem;
  }
  const content = parseContentObject(raw);
  if (!content) return undefined;
  if (content.pictureElem && typeof content.pictureElem === 'object') {
    return content.pictureElem as OpenImRawPictureElem;
  }
  // content itself is already a PictureElem shape (sourcePicture/bigPicture/...).
  if (content.sourcePicture || content.bigPicture || content.snapshotPicture || content.sourcePath) {
    return content as OpenImRawPictureElem;
  }
  return undefined;
}

function pickImageUrl(elem?: OpenImRawPictureElem): string | undefined {
  if (!elem) return undefined;
  const direct = firstHttpUrl(
    (elem as { url?: string }).url,
    (elem as { sourceUrl?: string }).sourceUrl,
    (elem as { bigUrl?: string }).bigUrl,
    (elem as { snapshotUrl?: string }).snapshotUrl,
  );
  if (direct) return direct;
  for (const key of ['sourcePicture', 'bigPicture', 'snapshotPicture'] as const) {
    const pic = asPictureInfo(elem[key]);
    const url = firstHttpUrl(pic?.url);
    if (url) return url;
  }
  return undefined;
}

function pickPictureDims(elem?: OpenImRawPictureElem): { width: number; height: number } {
  for (const key of ['bigPicture', 'sourcePicture', 'snapshotPicture'] as const) {
    const pic = asPictureInfo(elem?.[key]);
    const w = Number(pic?.width) || 0;
    const h = Number(pic?.height) || 0;
    if (w > 1 && h > 1) return { width: w, height: h };
  }
  return { width: 0, height: 0 };
}

function buildPhotoContent(raw: OpenImRawMessage): ApiMessage['content'] {
  const elem = resolvePictureElem(raw);
  const url = pickImageUrl(elem);
  const dims = pickPictureDims(elem);
  const picture = asPictureInfo(elem?.bigPicture) || asPictureInfo(elem?.sourcePicture);

  return {
    photo: {
      mediaType: 'photo',
      id: raw.clientMsgID,
      date: Math.floor(raw.sendTime / 1000),
      sizes: [],
      thumbnail: url
        ? { width: dims.width || picture?.width || 0, height: dims.height || picture?.height || 0, dataUri: url }
        : undefined,
      blobUrl: url,
    },
  };
}

function resolveSoundElem(raw: OpenImRawMessage): OpenImRawMessage['soundElem'] {
  if (raw.soundElem) return raw.soundElem;
  const content = parseContentObject(raw);
  if (content && (content.sourceUrl || content.soundPath || content.duration !== undefined)) {
    return content as OpenImRawMessage['soundElem'];
  }
  return undefined;
}

function buildVoiceContent(raw: OpenImRawMessage): ApiMessage['content'] {
  const elem = resolveSoundElem(raw);
  return {
    voice: {
      mediaType: 'voice',
      id: raw.clientMsgID,
      duration: elem?.duration ?? 0,
      size: elem?.dataSize ?? 0,
      blobUrl: resolveHttpMediaUrl(elem?.sourceUrl),
    },
  };
}

function resolveVideoElem(raw: OpenImRawMessage): OpenImRawMessage['videoElem'] {
  if (raw.videoElem) return raw.videoElem;
  const content = parseContentObject(raw);
  if (content && (content.videoUrl || content.snapshotUrl || content.duration !== undefined)) {
    return content as OpenImRawMessage['videoElem'];
  }
  return undefined;
}

function buildVideoContent(raw: OpenImRawMessage): ApiMessage['content'] {
  const elem = resolveVideoElem(raw);
  const videoUrl = resolveHttpMediaUrl(elem?.videoUrl);
  const snapshotUrl = resolveHttpMediaUrl(elem?.snapshotUrl);
  return {
    video: {
      mediaType: 'video',
      id: raw.clientMsgID,
      mimeType: elem?.videoType ? `video/${elem.videoType}` : 'video/mp4',
      duration: elem?.duration ?? 0,
      fileName: '',
      width: elem?.snapshotWidth,
      height: elem?.snapshotHeight,
      size: elem?.videoSize ?? 0,
      blobUrl: videoUrl,
      thumbnail: snapshotUrl
        ? { width: elem?.snapshotWidth || 0, height: elem?.snapshotHeight || 0, dataUri: snapshotUrl }
        : undefined,
    },
  };
}

function buildFileContent(raw: OpenImRawMessage): ApiMessage['content'] {
  const content = parseContentObject(raw) || {};
  const fileElem = (raw as { fileElem?: Record<string, unknown> }).fileElem
    || (content.fileElem as Record<string, unknown> | undefined)
    || content;
  const url = firstHttpUrl(fileElem.sourceUrl, fileElem.url);
  const fileName = cleanText(fileElem.fileName) || 'file';
  const size = Number(fileElem.fileSize) || 0;
  return {
    document: {
      mediaType: 'document',
      id: raw.clientMsgID || String(raw.seq || ''),
      size,
      fileName,
      mimeType: 'application/octet-stream',
      timestamp: Math.floor(raw.sendTime / 1000),
      // Same blobUrl escape hatch as photo/voice for OpenIM OSS URLs.
      ...(url ? { blobUrl: url } as any : {}),
    },
  };
}

function extractTextFromContentObject(obj: Record<string, unknown>): string {
  const directText = cleanText(obj.text);
  if (directText) return directText;

  const nestedQuote = obj.quoteElem;
  if (nestedQuote && typeof nestedQuote === 'object' && !Array.isArray(nestedQuote)) {
    const quoteText = cleanText((nestedQuote as { text?: unknown }).text);
    if (quoteText) return quoteText;
  }

  return cleanText(obj.content);
}

// Mirrors Android `TgImOpenImMessageMapper.textBody`.
export function extractOpenImTextBody(raw: OpenImRawMessage): string {
  const atText = cleanText(raw.atTextElem?.text);
  if (atText) return atText;

  const quoteText = cleanText(raw.quoteElem?.text);
  if (quoteText) return quoteText;

  const fromTextElem = cleanText(raw.textElem?.content);
  if (fromTextElem) return fromTextElem;

  const textElemRaw = (raw as { textElem?: unknown }).textElem;
  if (typeof textElemRaw === 'string') {
    const asObj = tryParseJsonObject(textElemRaw);
    const fromTe = cleanText(asObj?.content);
    if (fromTe) return fromTe;
    const plain = cleanText(textElemRaw);
    if (plain && !plain.startsWith('{')) return plain;
  }

  const contentObj = parseContentObject(raw);
  if (contentObj) {
    const fromObj = extractTextFromContentObject(contentObj);
    if (fromObj) return fromObj;
  } else if (typeof raw.content === 'string' && raw.content) {
    return raw.content;
  }

  return '';
}

import { assignOpenImMessageId, getOpenImMessageIdByClientMsgId } from './messageId';

// Kept for tests / callers that still need a stable hash without the time registry.
export function stableOpenImMessageId(clientMsgId: string): number {
  if (!clientMsgId) return 1;
  let hash = 0;
  for (let i = 0; i < clientMsgId.length; i++) {
    hash = (Math.imul(31, hash) + clientMsgId.charCodeAt(i)) | 0;
  }
  return (Math.abs(hash) % 900_000_000) + 1;
}

function resolveOpenImMessageId(raw: OpenImRawMessage): number {
  // Android `TgImOpenImMessageIdRegistry`: id must track send time, not raw seq/hash alone.
  return assignOpenImMessageId(raw);
}

function isOpenImOutgoingRead(raw: OpenImRawMessage, isOutgoing: boolean, isGroup: boolean): boolean {
  if (!isOutgoing) return false;
  if (!isGroup) {
    if (raw.isRead === true || raw.isRead === 1) return true;
  }
  const attached = (raw.attachedInfoElem || raw.attachedInfo) as
    | { groupHasReadInfo?: { hasReadCount?: number; unreadCount?: number; groupMemberCount?: number }; hasReadTime?: number }
    | undefined;
  const groupRead = attached?.groupHasReadInfo;
  if (groupRead) {
    const hasReadCount = groupRead.hasReadCount ?? 0;
    const unreadCount = groupRead.unreadCount ?? -1;
    const memberCount = groupRead.groupMemberCount ?? 0;
    if (hasReadCount > 0 && (unreadCount === 0 || (memberCount > 0 && hasReadCount >= memberCount - 1))) {
      return true;
    }
    if (hasReadCount > 0) return true;
  }
  const hasReadTime = attached?.hasReadTime || Number((raw as { hasReadTime?: unknown }).hasReadTime);
  return Number.isFinite(hasReadTime) && Number(hasReadTime) > 0;
}

function buildPrivateChatId(selfUserId: string, peerUserId: string): string {
  return `${OPENIM_PRIVATE_CHAT_ID_PREFIX}${[selfUserId, peerUserId].sort().join('_')}`;
}

// Shared three-client convention for grouping album photos (Android `TgImOpenImAlbumMetadata`,
// iOS `TgImOpenImAlbumMetadata.swift`): `ex` is a JSON string `{ tgimAlbum: { groupedId, order } }`.
// `groupedId` must round-trip as a string (Android writes `Long.toString`, iOS `String(Int64)`) —
// treating it as a JS `number` would lose precision on a real Int64 and desync the three clients.
function extractOpenImAlbumGroupedId(raw: OpenImRawMessage): string | undefined {
  if (typeof raw.ex !== 'string' || !raw.ex) return undefined;
  const parsed = tryParseJsonObject(raw.ex);
  const album = parsed?.tgimAlbum as { groupedId?: unknown } | undefined;
  return typeof album?.groupedId === 'string' && album.groupedId ? album.groupedId : undefined;
}

function isCustomRtcSignal(raw: OpenImRawMessage): boolean {
  const content = parseContentObject(raw);
  const dataRaw = content?.data ?? raw.content;
  let dataObj: Record<string, unknown> | undefined;
  if (typeof dataRaw === 'string') {
    dataObj = tryParseJsonObject(dataRaw);
  } else if (dataRaw && typeof dataRaw === 'object') {
    dataObj = dataRaw as Record<string, unknown>;
  }
  const customType = cleanText(dataObj?.customType) || cleanText(content?.customType);
  return customType === 'tgim_rtc_signal' || customType.includes('rtc');
}

function isCustomMessageEdit(raw: OpenImRawMessage): boolean {
  const content = parseContentObject(raw);
  const dataRaw = content?.data
    ?? (raw as { customElem?: { data?: unknown } }).customElem?.data
    ?? raw.content;
  let dataObj: Record<string, unknown> | undefined;
  if (typeof dataRaw === 'string') {
    dataObj = tryParseJsonObject(dataRaw);
  } else if (dataRaw && typeof dataRaw === 'object') {
    dataObj = dataRaw as Record<string, unknown>;
  }
  const customType = cleanText(dataObj?.customType) || cleanText(content?.customType);
  return customType === 'tgim_message_edit';
}

// Mirrors Android `shouldSkipDisplayMessage`: friend/system notifications (1200–2200) and RTC
// signaling (most contentType 110) must not become MessageUnsupported bubbles.
export function shouldSkipOpenImDisplayMessage(raw: OpenImRawMessage): boolean {
  const contentType = Number(raw.contentType) || 0;
  if (contentType >= 1200 && contentType <= 2200) {
    // Android `shouldSkipDisplayMessage`: group notifications 1500–1599 are NOT skipped
    // (`TgImOpenImGroupNotificationMapper.isGroupNotification` → return false).
    if (isOpenImGroupNotification(raw)) {
      return false;
    }
    return true;
  }
  if (contentType === OPENIM_CONTENT_TYPE_CUSTOM) {
    // Edit notifications (tgim_message_edit) must not become bubbles.
    if (isCustomMessageEdit(raw)) {
      return true;
    }
    return isCustomRtcSignal(raw);
  }
  // Deleted / revoked status (OpenIM MsgStatusHasDeleted ≈ 4 on some builds; Android checks explicitly).
  if (Number(raw.status) === 4) {
    return true;
  }
  return false;
}

/**
 * Maps an OpenIM MsgStruct to an ApiMessage.
 * Returns `undefined` for non-display system/signaling messages (Android skips these).
 */
export function mapOpenImMessageToApiMessage(
  raw: OpenImRawMessage,
  selfUserId: string,
): ApiMessage | undefined {
  if (shouldSkipOpenImDisplayMessage(raw)) {
    return undefined;
  }

  const isOutgoing = raw.sendID === selfUserId;
  const isGroupNotification = isOpenImGroupNotification(raw);
  const isGroupMessage = raw.sessionType === OPENIM_SESSION_TYPE_GROUP
    || raw.sessionType === OPENIM_SESSION_TYPE_SUPER_GROUP
    || (isGroupNotification && Boolean(raw.groupID));
  // This app only creates/joins WorkingGroup → conversationID is always `sg_` (see appDto/group.ts).
  // Mapping sessionType 2 → `g_` put history/realtime into a ghost chat while the UI used `sg_`.
  const chatId = isGroupMessage
    ? `sg_${raw.groupID}`
    : buildPrivateChatId(selfUserId, isOutgoing ? raw.recvID : raw.sendID);

  const contentType = Number(raw.contentType) || 0;
  const pictureElem = resolvePictureElem(raw);
  const pictureUrl = pickImageUrl(pictureElem);
  let content: ApiMessage['content'];
  if (isGroupNotification) {
    const groupContent = mapOpenImGroupNotificationContent(raw);
    if (!groupContent) {
      return undefined;
    }
    content = groupContent;
  } else if (contentType === OPENIM_CONTENT_TYPE_PICTURE || Boolean(pictureUrl)) {
    content = buildPhotoContent(raw);
    // Android: 102 without remote URL still shows a "[图片]" placeholder rather than Unsupported.
    if (content.photo && !content.photo.blobUrl) {
      content = { text: { text: '[图片]' } };
    }
  } else if (contentType === OPENIM_CONTENT_TYPE_SOUND) {
    content = buildVoiceContent(raw);
    if (!content.voice?.blobUrl) {
      content = { text: { text: '[语音]' } };
    }
  } else if (contentType === OPENIM_CONTENT_TYPE_VIDEO) {
    content = buildVideoContent(raw);
    if (!content.video?.blobUrl) {
      // Android drops empty video shells; keep a short placeholder instead of MessageUnsupported.
      content = { text: { text: '[视频]' } };
    }
  } else if (contentType === OPENIM_CONTENT_TYPE_FILE) {
    content = buildFileContent(raw);
  } else if (contentType === 112) {
    // Face / custom emoji (Android maps faceElem.data).
    const face = (raw as { faceElem?: { data?: string } }).faceElem
      || (parseContentObject(raw) as { data?: string } | undefined);
    const faceData = cleanText(face?.data) || extractOpenImTextBody(raw) || '[Emoji]';
    content = { text: { text: faceData } };
  } else if (contentType === OPENIM_CONTENT_TYPE_CUSTOM) {
    // Non-RTC custom (stickers/red-packet/etc.): if we cannot extract display text, skip rather
    // than emit MessageUnsupported. Stickers are not fully wired on web yet.
    const text = extractOpenImTextBody(raw);
    if (!text) {
      return undefined;
    }
    content = { text: { text } };
  } else {
    const text = extractOpenImTextBody(raw);
    if (!text) {
      return undefined;
    }
    content = { text: { text } };
  }

  // Incoming media that still has no usable body after placeholders above → skip.
  if (content.photo && !content.photo.blobUrl && !isOutgoing) {
    return undefined;
  }

  const replyInfo = extractOpenImReplyInfo(raw);
  const groupedId = extractOpenImAlbumGroupedId(raw);

  return {
    id: resolveOpenImMessageId(raw),
    chatId,
    content,
    date: Math.floor(raw.sendTime / 1000),
    isOutgoing,
    senderId: raw.sendID,
    // `selectCanForwardMessages` requires this to render the Forward action at all; OpenIM has no
    // per-message "forwarding disabled" concept of its own (chat-level/TTL restrictions are already
    // handled separately via `selectIsChatProtected`/`hasMessageTtl`, neither of which this mapper
    // sets), so every message defaults to forwardable, matching those other unset-means-unrestricted
    // fields. Left unset, the Forward button silently never appeared, for any chat or message type.
    isForwardingAllowed: true,
    ...(replyInfo ? { replyInfo } : {}),
    ...(groupedId ? { groupedId } : {}),
    ...(isOpenImOutgoingRead(raw, isOutgoing, isGroupMessage)
      ? { readDate: Math.floor(raw.sendTime / 1000) }
      : {}),
    // Keep full SDK MsgStruct for createQuoteMessage / createForwardMessage (Android raw cache).
    _tgimClientMsgId: raw.clientMsgID,
    _tgimServerMsgId: raw.serverMsgID,
    _tgimRaw: raw,
  } as ApiMessage;
}

function extractOpenImReplyInfo(raw: OpenImRawMessage): ApiMessage['replyInfo'] | undefined {
  const quoteElem = (raw as { quoteElem?: { quoteMessage?: OpenImRawMessage; text?: string } }).quoteElem;
  const contentObj = parseContentObject(raw) as { quoteMessage?: OpenImRawMessage } | undefined;
  const quoted = quoteElem?.quoteMessage || contentObj?.quoteMessage;
  if (!quoted || typeof quoted !== 'object') return undefined;

  const quotedClientId = cleanText((quoted as OpenImRawMessage).clientMsgID);
  let replyToMsgId = quotedClientId
    ? getOpenImMessageIdByClientMsgId(quotedClientId)
    : undefined;
  if (!replyToMsgId) {
    replyToMsgId = assignOpenImMessageId(quoted as OpenImRawMessage);
  }
  if (!replyToMsgId) return undefined;

  const quotePreview = extractOpenImTextBody(quoted as OpenImRawMessage)
    || cleanText((quoted as OpenImRawMessage).content)
    || '[消息]';

  return {
    type: 'message',
    replyToMsgId,
    quoteText: { text: quotePreview },
  };
}
